import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { convertToDeal } from "../lib/convertToDeal";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { SourcingLocked } from "./Sourcing";
import { Checkbox } from "../components/ui/checkbox";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Download, Layers, Loader2, Trash2, ArrowUp, ArrowDown, ArrowUpDown, Search, X } from "lucide-react";
import { toast } from "sonner";

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_META = {
    new:       { label: "New",       bg: "bg-blue-50",    text: "text-blue-700",    border: "border-blue-200",   chipInactive: "bg-white text-[#64748B] border-[#E2E8F0] hover:border-blue-200   hover:text-blue-700"   },
    reviewing: { label: "Reviewing", bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200",  chipInactive: "bg-white text-[#64748B] border-[#E2E8F0] hover:border-amber-200  hover:text-amber-700"  },
    contacted: { label: "Contacted", bg: "bg-purple-50",  text: "text-purple-700",  border: "border-purple-200", chipInactive: "bg-white text-[#64748B] border-[#E2E8F0] hover:border-purple-200 hover:text-purple-700" },
    mandated:  { label: "Mandated",  bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", chipInactive: "bg-white text-[#64748B] border-[#E2E8F0] hover:border-emerald-200 hover:text-emerald-700" },
    rejected:  { label: "Rejected",  bg: "bg-red-50",     text: "text-red-600",     border: "border-red-200",    chipInactive: "bg-white text-[#64748B] border-[#E2E8F0] hover:border-red-200   hover:text-red-600"    },
};
const STATUSES = Object.keys(STATUS_META);

// ── Helpers ───────────────────────────────────────────────────────────────────

function ageYears(incorporationDate) {
    if (!incorporationDate) return null;
    try {
        const diff = (Date.now() - new Date(incorporationDate).getTime()) / (1000 * 60 * 60 * 24 * 365.25);
        return Math.floor(diff);
    } catch { return null; }
}

function sicLabel(c) {
    const divs = c.sic_divisions;
    const codes = c.sic_codes;
    if (divs?.length) return `SIC ${divs.slice(0, 2).join(", ")}`;
    if (codes?.length) return codes[0];
    return "—";
}

function csvEscape(val) {
    const s = (val == null || val === "—") ? "" : String(val);
    return `"${s.replace(/"/g, '""')}"`;
}

// ── Status select (inline badge-style) ───────────────────────────────────────

function StatusSelect({ candidate, onUpdate }) {
    const m = STATUS_META[candidate.status] || STATUS_META.new;
    return (
        <Select
            value={candidate.status}
            onValueChange={v => onUpdate(candidate.company_number, v)}
        >
            <SelectTrigger
                data-testid={`status-select-${candidate.company_number}`}
                className={`h-7 text-[11px] font-medium rounded-full border px-2.5 w-auto gap-1.5 ${m.bg} ${m.text} ${m.border} hover:opacity-90 transition-opacity`}
            >
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                {STATUSES.map(s => {
                    const sm = STATUS_META[s];
                    return (
                        <SelectItem key={s} value={s} className="text-xs">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${sm.bg} ${sm.text} ${sm.border} border`}>
                                {sm.label}
                            </span>
                        </SelectItem>
                    );
                })}
            </SelectContent>
        </Select>
    );
}

// ── Inline notes cell ─────────────────────────────────────────────────────────

function NotesCell({ candidate, onSave }) {
    const [editing, setEditing] = useState(false);
    const [val, setVal] = useState(candidate.notes || "");

    useEffect(() => {
        if (!editing) setVal(candidate.notes || "");
    }, [candidate.notes, editing]);

    const commit = () => {
        setEditing(false);
        if (val !== (candidate.notes || "")) {
            onSave(candidate.company_number, val);
        }
    };

    if (editing) {
        return (
            <Input
                autoFocus
                value={val}
                onChange={e => setVal(e.target.value)}
                onBlur={commit}
                onKeyDown={e => {
                    if (e.key === "Enter") commit();
                    if (e.key === "Escape") { setVal(candidate.notes || ""); setEditing(false); }
                }}
                onClick={e => e.stopPropagation()}
                className="h-7 text-xs py-0 min-w-[180px]"
                data-testid={`notes-input-${candidate.company_number}`}
            />
        );
    }
    return (
        <button
            data-testid={`notes-cell-${candidate.company_number}`}
            onClick={() => setEditing(true)}
            className="text-left text-xs text-[#64748B] hover:text-[#0B1F3A] truncate max-w-[220px] block transition-colors"
            title={val || "Click to add notes"}
        >
            {val || <span className="text-[#CBD5E1] italic">Add notes…</span>}
        </button>
    );
}

// ── Filter chips ──────────────────────────────────────────────────────────────

function FilterChips({ activeFilter, counts, total, onSelect }) {
    const chips = [
        {
            key: null,
            label: "All",
            count: total,
            activeCls: "bg-[#0B1F3A] text-white border-[#0B1F3A]",
            inactiveCls: "bg-white text-[#475569] border-[#E2E8F0] hover:border-[#0B1F3A] hover:text-[#0B1F3A]",
        },
        ...STATUSES.map(s => ({
            key: s,
            label: STATUS_META[s].label,
            count: counts[s],
            activeCls: `${STATUS_META[s].bg} ${STATUS_META[s].text} ${STATUS_META[s].border}`,
            inactiveCls: STATUS_META[s].chipInactive,
        })),
    ];

    return (
        <div className="flex items-center gap-2 flex-wrap" data-testid="pipeline-filter-bar">
            {chips.map(chip => {
                const isActive = activeFilter === chip.key;
                return (
                    <button
                        key={chip.key ?? "all"}
                        data-testid={`filter-chip-${chip.key ?? "all"}`}
                        onClick={() => onSelect(isActive && chip.key !== null ? null : chip.key)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-medium border transition-all ${isActive ? chip.activeCls : chip.inactiveCls}`}
                    >
                        {chip.label}
                        <span className={`font-mono text-[10px] ${isActive ? "opacity-75" : "opacity-55"}`}>
                            {chip.count}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

// ── Empty state ───────────────────────────────────────────────────────────────

function EmptyPipeline() {
    return (
        <div
            data-testid="pipeline-empty-state"
            className="border border-dashed border-[#CBD5E1] rounded-[6px] bg-white p-12 text-center"
        >
            <div
                className="w-12 h-12 flex items-center justify-center rounded-[8px] mx-auto mb-4"
                style={{ background: 'rgba(11,31,58,0.06)' }}
            >
                <Layers className="w-6 h-6 text-[#94A3B8]" strokeWidth={1.75} />
            </div>
            <h3 className="text-[15px] font-semibold text-[#0B1F3A] mb-1.5">No companies in pipeline yet</h3>
            <p className="text-sm text-[#64748B] mb-6 max-w-sm mx-auto">
                Run a mandate from the Mandates page to find matching companies, then add them to your pipeline.
            </p>
            <Link to="/sourcing/mandates">
                <Button style={{ background: '#0B1F3A', color: '#FFFFFF' }}>
                    Go to Mandates
                </Button>
            </Link>
        </div>
    );
}

// ── Main pipeline view ────────────────────────────────────────────────────────

function SourcingPipelineView() {
    const nav = useNavigate();
    const { hasSurface } = useAuth();
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [removing, setRemoving] = useState({});
    const [activeFilter, setActiveFilter] = useState(null);
    const [selectedNumbers, setSelectedNumbers] = useState(new Set());
    const [bulkStatus, setBulkStatus] = useState("");
    const [bulkApplying, setBulkApplying] = useState(false);
    const [sortCol, setSortCol] = useState(null);   // null | "company_name" | "status" | "added_at"
    const [sortDir, setSortDir] = useState(null);   // null | "asc" | "desc"
    const [showBulkRemoveDialog, setShowBulkRemoveDialog] = useState(false);
    const [bulkRemoving, setBulkRemoving] = useState(false);
    const [pageSize, setPageSize] = useState(25);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchText, setSearchText] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // Debounce search input — 250ms
    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch(searchText);
            setCurrentPage(1);
        }, 250);
        return () => clearTimeout(t);
    }, [searchText]);

    const handleClearSearch = () => {
        setSearchText("");
        setDebouncedSearch(""); // immediate, no wait
        setCurrentPage(1);
    };

    useEffect(() => {
        api.get("/sourcing/")
            .then(r => setCandidates(r.data.candidates || []))
            .catch(() => setCandidates([]))
            .finally(() => setLoading(false));
    }, []);

    const handleUpdateStatus = async (companyNumber, newStatus) => {
        const original = candidates.find(c => c.company_number === companyNumber)?.status;
        setCandidates(cs => cs.map(c => c.company_number === companyNumber ? { ...c, status: newStatus } : c));
        try {
            await api.patch(`/sourcing/candidates/${companyNumber}`, { status: newStatus });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to update status");
            setCandidates(cs => cs.map(c => c.company_number === companyNumber ? { ...c, status: original } : c));
        }
    };

    const handleUpdateNotes = async (companyNumber, notes) => {
        setCandidates(cs => cs.map(c => c.company_number === companyNumber ? { ...c, notes } : c));
        try {
            await api.patch(`/sourcing/candidates/${companyNumber}`, { notes });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to save notes");
        }
    };

    const handleRemove = async (companyNumber) => {
        setRemoving(prev => ({ ...prev, [companyNumber]: true }));
        try {
            await api.delete(`/sourcing/candidates/${companyNumber}`);
            setCandidates(cs => cs.filter(c => c.company_number !== companyNumber));
            setSelectedNumbers(prev => { const n = new Set(prev); n.delete(companyNumber); return n; });
            toast.success("Removed from pipeline");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to remove");
            setRemoving(prev => ({ ...prev, [companyNumber]: false }));
        }
    };

    // Reset selection when filter changes
    const handleFilterSelect = (filter) => {
        setActiveFilter(filter);
        setSelectedNumbers(new Set());
        setCurrentPage(1);
    };

    // Three-state sort toggle: asc → desc → default; always resets to page 1
    const handleSort = (col) => {
        if (sortCol !== col) {
            setSortCol(col);
            setSortDir("asc");
        } else if (sortDir === "asc") {
            setSortDir("desc");
        } else {
            setSortCol(null);
            setSortDir(null);
        }
        setCurrentPage(1);
    };

    // Status summary counts (always from full list)
    const counts = STATUSES.reduce((acc, s) => ({
        ...acc, [s]: candidates.filter(c => c.status === s).length
    }), {});

    // Client-side filter — status chip + search text (AND composition)
    const filtered = (() => {
        let r = activeFilter ? candidates.filter(c => c.status === activeFilter) : candidates;
        if (debouncedSearch.trim()) {
            const q = debouncedSearch.trim().toLowerCase();
            r = r.filter(c => (c.company_name || "").toLowerCase().includes(q));
        }
        return r;
    })();

    // Client-side sort — applied after filter; preserves insertion order when no sort active
    const visibleCandidates = (() => {
        if (!sortCol || !sortDir) return filtered;
        return [...filtered].sort((a, b) => {
            let aVal, bVal;
            if (sortCol === "company_name") {
                aVal = (a.company_name || "").toLowerCase();
                bVal = (b.company_name || "").toLowerCase();
            } else if (sortCol === "status") {
                aVal = STATUSES.indexOf(a.status ?? "");
                bVal = STATUSES.indexOf(b.status ?? "");
                if (aVal === -1) aVal = 999;
                if (bVal === -1) bVal = 999;
            } else { // added_at
                aVal = a.added_at ? new Date(a.added_at).getTime() : 0;
                bVal = b.added_at ? new Date(b.added_at).getTime() : 0;
            }
            if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
            if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
            return 0;
        });
    })();

    // Pagination — applied after filter+sort; CSV export uses full visibleCandidates
    const totalRows = visibleCandidates.length;
    const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
    const safePage = Math.min(currentPage, totalPages);
    const pagedCandidates = visibleCandidates.slice((safePage - 1) * pageSize, safePage * pageSize);

    // Page navigation — clears selection so users see only what's on the new page
    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        setSelectedNumbers(new Set());
    };

    // Selection computed values (scoped to current page only)
    const allSelected = pagedCandidates.length > 0 && pagedCandidates.every(c => selectedNumbers.has(c.company_number));
    const someSelected = pagedCandidates.some(c => selectedNumbers.has(c.company_number)) && !allSelected;
    const headerChecked = allSelected ? true : someSelected ? "indeterminate" : false;
    const selectedCount = pagedCandidates.filter(c => selectedNumbers.has(c.company_number)).length;

    const toggleSelect = (num) => setSelectedNumbers(prev => {
        const next = new Set(prev);
        next.has(num) ? next.delete(num) : next.add(num);
        return next;
    });

    const handleSelectAll = () => {
        setSelectedNumbers(
            allSelected ? new Set() : new Set(pagedCandidates.map(c => c.company_number))
        );
    };

    const handleBulkStatusUpdate = async () => {
        const numbers = pagedCandidates
            .filter(c => selectedNumbers.has(c.company_number))
            .map(c => c.company_number);
        if (!numbers.length || !bulkStatus) return;
        setBulkApplying(true);
        try {
            const { data } = await api.patch("/sourcing/pipeline/bulk-status", {
                company_numbers: numbers,
                status: bulkStatus,
            });
            setCandidates(cs => cs.map(c =>
                numbers.includes(c.company_number) ? { ...c, status: bulkStatus } : c
            ));
            toast.success(
                `${data.updated_count} compan${data.updated_count === 1 ? "y" : "ies"} set to ${STATUS_META[bulkStatus]?.label}`
            );
            setSelectedNumbers(new Set());
            setBulkStatus("");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Bulk status update failed");
        } finally {
            setBulkApplying(false);
        }
    };

    const handleBulkRemoveConfirm = async () => {
        const numbers = pagedCandidates
            .filter(c => selectedNumbers.has(c.company_number))
            .map(c => c.company_number);
        if (!numbers.length) return;
        setBulkRemoving(true);
        try {
            const { data } = await api.post("/sourcing/pipeline/bulk-remove", {
                company_numbers: numbers,
            });
            setCandidates(cs => cs.filter(c => !numbers.includes(c.company_number)));
            setSelectedNumbers(new Set());
            setShowBulkRemoveDialog(false);
            toast.success(
                `${data.removed_count} compan${data.removed_count === 1 ? "y" : "ies"} removed from pipeline`
            );
        } catch (err) {
            toast.error(err.response?.data?.detail || "Bulk remove failed");
        } finally {
            setBulkRemoving(false);
        }
    };

    const handleExportCsv = () => {
        const header = ["Company Name", "Company Number", "Status", "SIC Code", "Location", "Notes", "Date Added"];
        const rows = visibleCandidates.map(c => [
            csvEscape(c.company_name),
            csvEscape(c.company_number),
            csvEscape(STATUS_META[c.status]?.label || c.status),
            csvEscape(sicLabel(c)),
            csvEscape(c.registered_county || c.registered_country),
            csvEscape(c.notes),
            csvEscape(c.added_at ? new Date(c.added_at).toISOString().slice(0, 10) : ""),
        ]);
        const csv = [header.map(csvEscape), ...rows].map(r => r.join(",")).join("\r\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const dateStr = new Date().toISOString().slice(0, 10);
        const filterKey = activeFilter || "all";
        a.href = url;
        a.download = `closvio-pipeline-${filterKey}-${dateStr}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <>
        <div data-testid="sourcing-pipeline-page">
            <PageHeader title="Pipeline" subtitle="Companies added from mandate runs." />

            <div className="p-6 lg:p-8 space-y-6 bg-[#F8FAFC] min-h-[calc(100vh-44px)]">
                {/* Status strip */}
                {!loading && candidates.length > 0 && (
                    <div className="grid grid-cols-5 gap-3" data-testid="pipeline-status-strip">
                        {STATUSES.map(s => {
                            const m = STATUS_META[s];
                            return (
                                <div
                                    key={s}
                                    data-testid={`pipeline-stat-${s}`}
                                    className={`border rounded-[6px] px-4 py-3 ${m.bg} ${m.border}`}
                                >
                                    <p className={`text-[10px] uppercase tracking-wider font-semibold ${m.text} opacity-70`}>
                                        {m.label}
                                    </p>
                                    <p className={`text-2xl font-bold font-mono mt-0.5 ${m.text}`}>
                                        {counts[s]}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Table / loading / empty */}
                {loading ? (
                    <div className="py-16 flex justify-center" data-testid="pipeline-loading">
                        <Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" />
                    </div>
                ) : candidates.length === 0 ? (
                    <EmptyPipeline />
                ) : (
                    <>
                        {/* Search + Filter chips row */}
                        <div className="flex flex-wrap items-start gap-3">
                            {/* Search input */}
                            <div className="relative shrink-0">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#94A3B8] pointer-events-none" />
                                <input
                                    type="text"
                                    value={searchText}
                                    onChange={e => setSearchText(e.target.value)}
                                    placeholder="Search by company name…"
                                    data-testid="pipeline-search-input"
                                    className="h-8 pl-8 pr-7 text-[12px] border border-[#E2E8F0] rounded-[6px] bg-white text-[#0B1F3A] placeholder:text-[#CBD5E1] focus:outline-none focus:border-[#0B1F3A] transition-colors w-[220px]"
                                />
                                {searchText && (
                                    <button
                                        onClick={handleClearSearch}
                                        data-testid="pipeline-search-clear"
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#0B1F3A]"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                )}
                            </div>
                            {/* Filter chips */}
                            <FilterChips
                                activeFilter={activeFilter}
                                counts={counts}
                                total={candidates.length}
                                onSelect={handleFilterSelect}
                            />
                        </div>

                        {/* Table */}
                        <div
                            className="border border-[#E2E8F0] rounded-[6px] bg-white overflow-hidden"
                            data-testid="pipeline-table"
                        >
                            {/* Table toolbar */}
                            <div className="px-5 py-3.5 border-b border-[#E2E8F0] flex items-center justify-between bg-[#F8FAFC]">
                                <div className="flex items-center gap-2">
                                    <h2 className="text-sm font-semibold text-[#0B1F3A]">Candidates</h2>
                                    {activeFilter && (
                                        <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium border ${STATUS_META[activeFilter].bg} ${STATUS_META[activeFilter].text} ${STATUS_META[activeFilter].border}`}>
                                            {STATUS_META[activeFilter].label}
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-xs text-[#94A3B8]" data-testid="pipeline-table-count">
                                        {visibleCandidates.length !== candidates.length
                                            ? `${visibleCandidates.length} of ${candidates.length}`
                                            : `${candidates.length} total`}
                                        {totalPages > 1 && ` · pg ${safePage}/${totalPages}`}
                                    </span>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        data-testid="pipeline-export-csv-button"
                                        disabled={visibleCandidates.length === 0}
                                        onClick={handleExportCsv}
                                        className="h-7 text-[11px] px-2.5 gap-1.5 border-[#E2E8F0] text-[#475569] hover:text-[#0B1F3A] hover:border-[#0B1F3A] disabled:opacity-40"
                                    >
                                        <Download className="w-3 h-3" />
                                        Export CSV
                                    </Button>
                                </div>
                            </div>

                            {/* Bulk action bar — visible when ≥1 row selected */}
                            {selectedCount > 0 && (
                                <div
                                    className="px-5 py-2.5 border-b border-[#E2E8F0] bg-[#EFF6FF] flex items-center justify-between gap-3"
                                    data-testid="pipeline-bulk-action-bar"
                                >
                                    <span className="text-[12px] font-medium text-blue-700" data-testid="pipeline-bulk-selected-count">
                                        {selectedCount} selected
                                    </span>
                                    <div className="flex items-center gap-2.5">
                                        <button
                                            onClick={() => setSelectedNumbers(new Set())}
                                            data-testid="pipeline-bulk-clear-btn"
                                            className="text-[12px] text-[#64748B] hover:text-[#0B1F3A] underline"
                                        >
                                            Clear
                                        </button>
                                        <Select value={bulkStatus} onValueChange={setBulkStatus}>
                                            <SelectTrigger
                                                data-testid="pipeline-bulk-status-select"
                                                className={`h-7 text-[11px] font-medium rounded-full border px-2.5 w-auto gap-1.5 transition-colors ${
                                                    bulkStatus
                                                        ? `${STATUS_META[bulkStatus].bg} ${STATUS_META[bulkStatus].text} ${STATUS_META[bulkStatus].border}`
                                                        : "bg-white text-[#64748B] border-[#CBD5E1]"
                                                }`}
                                            >
                                                <SelectValue placeholder="Set status…" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {STATUSES.map(s => {
                                                    const sm = STATUS_META[s];
                                                    return (
                                                        <SelectItem key={s} value={s} className="text-xs">
                                                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${sm.bg} ${sm.text} ${sm.border} border`}>
                                                                {sm.label}
                                                            </span>
                                                        </SelectItem>
                                                    );
                                                })}
                                            </SelectContent>
                                        </Select>
                                        <Button
                                            size="sm"
                                            data-testid="pipeline-bulk-apply-btn"
                                            disabled={!bulkStatus || bulkApplying}
                                            onClick={handleBulkStatusUpdate}
                                            className="h-7 text-[12px] px-3"
                                            style={{ background: '#0B1F3A', color: '#FFFFFF' }}
                                        >
                                            {bulkApplying
                                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                                : "Apply"
                                            }
                                        </Button>
                                        {/* Separator */}
                                        <div className="w-px h-5 bg-blue-200" />
                                        {/* Remove action */}
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            data-testid="pipeline-bulk-remove-btn"
                                            onClick={() => setShowBulkRemoveDialog(true)}
                                            className="h-7 text-[12px] px-3 gap-1.5 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                                        >
                                            <Trash2 className="w-3 h-3" />
                                            Remove from Pipeline
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {visibleCandidates.length === 0 ? (
                                <div className="py-12 text-center" data-testid="filter-no-results">
                                    <p className="text-sm text-[#94A3B8]">
                                        {debouncedSearch
                                            ? `No companies match "${debouncedSearch}"${activeFilter ? ` with status "${STATUS_META[activeFilter]?.label}"` : ""}`
                                            : `No companies with status "${STATUS_META[activeFilter]?.label}"`
                                        }
                                    </p>
                                    <div className="flex items-center justify-center gap-3 mt-1.5">
                                        {debouncedSearch && (
                                            <button
                                                onClick={handleClearSearch}
                                                className="text-xs text-[#0B1F3A] underline hover:opacity-70"
                                                data-testid="search-clear-btn-empty"
                                            >
                                                Clear search
                                            </button>
                                        )}
                                        {activeFilter && (
                                            <button
                                                onClick={() => handleFilterSelect(null)}
                                                className="text-xs text-[#0B1F3A] underline hover:opacity-70"
                                                data-testid="filter-clear-btn"
                                            >
                                                Clear filter
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <>
                                <table className="w-full text-[13px]">
                                    <thead className="border-b border-[#E2E8F0]">
                                        <tr>
                                            <th className="w-10 px-3 py-2.5" onClick={e => e.stopPropagation()}>
                                                <Checkbox
                                                    data-testid="pipeline-select-all-checkbox"
                                                    title="Select all on this page"
                                                    checked={headerChecked}
                                                    onCheckedChange={handleSelectAll}
                                                    className="border-[#CBD5E1]"
                                                />
                                            </th>
                                            {/* Sortable: Company Name */}
                                            <th
                                                data-testid="sort-header-company"
                                                onClick={() => handleSort("company_name")}
                                                className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider whitespace-nowrap cursor-pointer select-none group text-[#94A3B8] hover:text-[#0B1F3A]"
                                            >
                                                <span className="inline-flex items-center gap-1">
                                                    Company
                                                    {sortCol === "company_name" && sortDir === "asc"  && <ArrowUp   className="w-3 h-3 text-[#0B1F3A]" />}
                                                    {sortCol === "company_name" && sortDir === "desc" && <ArrowDown className="w-3 h-3 text-[#0B1F3A]" />}
                                                    {sortCol !== "company_name" && <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-40 transition-opacity" />}
                                                </span>
                                            </th>
                                            {/* Static */}
                                            <th className="px-4 py-2.5 text-left text-[11px] font-medium text-[#94A3B8] uppercase tracking-wider whitespace-nowrap">SIC / Sector</th>
                                            <th className="px-4 py-2.5 text-left text-[11px] font-medium text-[#94A3B8] uppercase tracking-wider whitespace-nowrap">Incorporated</th>
                                            <th className="px-4 py-2.5 text-left text-[11px] font-medium text-[#94A3B8] uppercase tracking-wider whitespace-nowrap">Location</th>
                                            <th className="px-4 py-2.5 text-left text-[11px] font-medium text-[#94A3B8] uppercase tracking-wider whitespace-nowrap">Owner age</th>
                                            {/* Sortable: Status */}
                                            <th
                                                data-testid="sort-header-status"
                                                onClick={() => handleSort("status")}
                                                className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider whitespace-nowrap cursor-pointer select-none group text-[#94A3B8] hover:text-[#0B1F3A]"
                                            >
                                                <span className="inline-flex items-center gap-1">
                                                    Status
                                                    {sortCol === "status" && sortDir === "asc"  && <ArrowUp   className="w-3 h-3 text-[#0B1F3A]" />}
                                                    {sortCol === "status" && sortDir === "desc" && <ArrowDown className="w-3 h-3 text-[#0B1F3A]" />}
                                                    {sortCol !== "status" && <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-40 transition-opacity" />}
                                                </span>
                                            </th>
                                            {/* Static */}
                                            <th className="px-4 py-2.5 text-left text-[11px] font-medium text-[#94A3B8] uppercase tracking-wider whitespace-nowrap">Notes</th>
                                            {/* Sortable: Date Added */}
                                            <th
                                                data-testid="sort-header-added"
                                                onClick={() => handleSort("added_at")}
                                                className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider whitespace-nowrap cursor-pointer select-none group text-[#94A3B8] hover:text-[#0B1F3A]"
                                            >
                                                <span className="inline-flex items-center gap-1">
                                                    Added
                                                    {sortCol === "added_at" && sortDir === "asc"  && <ArrowUp   className="w-3 h-3 text-[#0B1F3A]" />}
                                                    {sortCol === "added_at" && sortDir === "desc" && <ArrowDown className="w-3 h-3 text-[#0B1F3A]" />}
                                                    {sortCol !== "added_at" && <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-40 transition-opacity" />}
                                                </span>
                                            </th>
                                            {/* Action */}
                                            <th className="px-4 py-2.5" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pagedCandidates.map(c => {
                                            const age = ageYears(c.incorporation_date);
                                            return (
                                                <tr
                                                    key={c.company_number}
                                                    data-testid={`pipeline-row-${c.company_number}`}
                                                    className={`border-t border-[#F1F5F9] hover:bg-[#F8FAFC] ${selectedNumbers.has(c.company_number) ? "bg-blue-50/40" : ""}`}
                                                >
                                                    <td className="w-10 px-3 py-3" onClick={e => e.stopPropagation()}>
                                                        <Checkbox
                                                            data-testid={`pipeline-select-checkbox-${c.company_number}`}
                                                            checked={selectedNumbers.has(c.company_number)}
                                                            onCheckedChange={() => toggleSelect(c.company_number)}
                                                            className="border-[#CBD5E1]"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3 max-w-[220px]">
                                                        <Link to={`/sourcing/company/${c.company_number}`} className="font-medium text-[#0B1F3A] truncate block hover:underline" title={c.company_name}>{c.company_name || "—"}</Link>
                                                        {c.company_number && (
                                                            <a
                                                                data-testid={`ch-link-pipeline-${c.company_number}`}
                                                                href={`https://find-and-update.company-information.service.gov.uk/company/${c.company_number}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="font-mono text-[11px] text-[#64748B] hover:text-[#0B1F3A] hover:underline mt-0.5 block"
                                                                onClick={e => e.stopPropagation()}
                                                            >
                                                                {c.company_number}
                                                            </a>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className="font-mono text-[11px] text-[#64748B]">{sicLabel(c)}</span>
                                                    </td>
                                                    <td className="px-4 py-3 text-[#64748B] whitespace-nowrap">
                                                        {age != null ? `${age} yr` : "—"}
                                                    </td>
                                                    <td className="px-4 py-3 text-[#64748B] whitespace-nowrap">
                                                        {c.registered_county || c.registered_country || "—"}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap" data-testid={`pipeline-owner-age-${c.company_number}`}>
                                                        {c.psc_summary?.oldest_age != null ? (
                                                            <span className="inline-flex items-center gap-1.5">
                                                                <span className="font-mono text-[12px] text-[#0B1F3A]">~{c.psc_summary.oldest_age}</span>
                                                                {c.psc_summary.succession_flag && (
                                                                    <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">Succession</span>
                                                                )}
                                                            </span>
                                                        ) : <span className="text-[#CBD5E1]">—</span>}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <StatusSelect candidate={c} onUpdate={handleUpdateStatus} />
                                                    </td>
                                                    <td className="px-4 py-3 max-w-[240px]">
                                                        <NotesCell candidate={c} onSave={handleUpdateNotes} />
                                                    </td>
                                                    <td className="px-4 py-3 text-[#94A3B8] text-xs whitespace-nowrap">
                                                        {c.added_at
                                                            ? new Date(c.added_at).toLocaleDateString("en-GB")
                                                            : "—"}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap">
                                                        {hasSurface("deal_management") && (
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="h-7 mr-1 text-[11px]"
                                                                data-testid={`pipeline-convert-btn-${c.company_number}`}
                                                                onClick={() => convertToDeal(`/sourcing/candidates/${c.company_number}/convert-to-deal`, nav)}
                                                            >
                                                                {c.deal_id ? "Open deal" : "Convert to deal"}
                                                            </Button>
                                                        )}
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            data-testid={`pipeline-remove-btn-${c.company_number}`}
                                                            disabled={removing[c.company_number]}
                                                            onClick={() => handleRemove(c.company_number)}
                                                            className="h-7 w-7 p-0 text-[#CBD5E1] hover:text-red-500 hover:bg-red-50"
                                                        >
                                                            {removing[c.company_number]
                                                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                                : <Trash2 className="w-3.5 h-3.5" />
                                                            }
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                {/* Pagination controls */}
                                {totalPages > 1 && (
                                    <div className="px-5 py-3 border-t border-[#E2E8F0] flex items-center justify-between bg-[#F8FAFC]">
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs text-[#94A3B8]" data-testid="pagination-info">
                                                Page {safePage} of {totalPages} · {totalRows} companies
                                            </span>
                                            <Select
                                                value={String(pageSize)}
                                                onValueChange={v => { setPageSize(Number(v)); setCurrentPage(1); setSelectedNumbers(new Set()); }}
                                            >
                                                <SelectTrigger
                                                    data-testid="pagination-page-size-select"
                                                    className="h-6 text-[11px] px-2 w-auto gap-1 border-[#E2E8F0] text-[#64748B]"
                                                >
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {[25, 50, 100].map(n => (
                                                        <SelectItem key={n} value={String(n)} className="text-xs">{n} / page</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                data-testid="pagination-prev-btn"
                                                disabled={safePage === 1}
                                                onClick={() => handlePageChange(safePage - 1)}
                                                className="h-7 px-2.5 text-[12px]"
                                            >
                                                Previous
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                data-testid="pagination-next-btn"
                                                disabled={safePage === totalPages}
                                                onClick={() => handlePageChange(safePage + 1)}
                                                className="h-7 px-2.5 text-[12px]"
                                            >
                                                Next
                                            </Button>
                                        </div>
                                    </div>
                                )}
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>

        {/* Bulk Remove confirmation dialog */}
        <AlertDialog open={showBulkRemoveDialog} onOpenChange={setShowBulkRemoveDialog}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>
                        Remove {selectedCount} compan{selectedCount === 1 ? "y" : "ies"} from the pipeline?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                        This will permanently remove {selectedCount} compan{selectedCount === 1 ? "y" : "ies"} from your pipeline. This cannot be undone.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel data-testid="bulk-remove-cancel-btn">
                        Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                        data-testid="bulk-remove-confirm-btn"
                        disabled={bulkRemoving}
                        onClick={handleBulkRemoveConfirm}
                        className="bg-red-600 hover:bg-red-700 text-white"
                    >
                        {bulkRemoving
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : `Remove ${selectedCount}`
                        }
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
        </>
    );
}

// ── Page entry point ──────────────────────────────────────────────────────────

export default function SourcingPipeline() {
    const { hasSurface } = useAuth();
    return hasSurface("sourcing") ? <SourcingPipelineView /> : <SourcingLocked />;
}
